import { useState } from "react";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import CloseIcon from "@mui/icons-material/Close";
import FieldLabel from "../FieldLabel/FieldLabel";
import "./SelectInput.scss";

const SEARCHABLE_THRESHOLD = 8;
const EMPTY_OPTIONS = [];

const MultiSelectInput = ({
  title,
  name,
  value,
  onChange,
  options = EMPTY_OPTIONS,
  placeholder = "Please select",
  disabled = false,
  style,
}) => {
  const [filterText, setFilterText] = useState("");

  const selectedValues = Array.isArray(value)
    ? value.map((v) => String(v))
    : value !== undefined && value !== null && value !== ""
    ? [String(value)]
    : [];

  const filteredOptions = options.filter((opt) =>
    opt.label.toLowerCase().includes(filterText.toLowerCase())
  );

  return (
    <div className="select-input">
      {title && <FieldLabel htmlFor={name}>{title}</FieldLabel>}
      <Select
        id={name}
        name={name}
        multiple
        size="small"
        value={selectedValues}
        inputProps={{ "aria-label": title || placeholder }}
        onChange={(e) => {
          const val = e.target.value;
          const newValues = typeof val === "string" ? val.split(",") : val;
          onChange({ target: { name, value: newValues } });
        }}
        displayEmpty
        disabled={disabled}
        fullWidth
        renderValue={(selected) => {
          if (!selected || selected.length === 0) {
            return <span style={{ color: "#838383" }}>{placeholder}</span>;
          }
          return (
            <span style={{ color: "#104581", fontWeight: 600 }}>
              {selected.length} Selected
            </span>
          );
        }}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 340,
              borderRadius: 10,
              boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
            },
          },
          autoFocus: false,
        }}
        sx={{ height: 40, minWidth: 220, ...style }}
      >
        <ListSubheader
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 1,
            backgroundColor: "#ffffff",
            padding: "8px 12px",
            borderBottom: "1px solid #f1f5f9",
          }}
        >
          <TextField
            size="small"
            fullWidth
            placeholder="Type to filter..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          />
        </ListSubheader>

        {filteredOptions.length > 0 ? (
          filteredOptions.map((option) => {
            const optValStr = String(option.value);
            const isChecked = selectedValues.includes(optValStr);
            return (
              <MenuItem key={option.value} value={optValStr}>
                <Checkbox
                  checked={isChecked}
                  size="small"
                  sx={{
                    padding: "2px 8px 2px 0",
                    color: "#104581",
                    "&.Mui-checked": { color: "#104581" },
                  }}
                />
                <ListItemText primary={option.label} />
              </MenuItem>
            );
          })
        ) : (
          <MenuItem disabled>No matching options</MenuItem>
        )}
      </Select>
    </div>
  );
};

const SelectInput = ({
  title,
  name,
  value,
  onChange,
  options = EMPTY_OPTIONS,
  placeholder = "Please select",
  disabled = false,
  clearable = false,
  multiple = false,
  style,
}) => {
  if (multiple) {
    return (
      <MultiSelectInput
        title={title}
        name={name}
        value={value}
        onChange={onChange}
        options={options}
        placeholder={placeholder}
        disabled={disabled}
        style={style}
      />
    );
  }

  const searchable = options.length > SEARCHABLE_THRESHOLD;
  const selectValue = options.some((option) => String(option.value) === String(value))
    ? value
    : "";

  return (
    <div className="select-input">
      {title && <FieldLabel htmlFor={name}>{title}</FieldLabel>}
      {searchable ? (
        <Autocomplete
          id={name}
          size="small"
          disabled={disabled}
          options={options}
          disableClearable={!clearable}
          getOptionLabel={(option) => option?.label ?? ""}
          isOptionEqualToValue={(option, val) => option.value === val.value}
          value={
            options.find((option) => String(option.value) === String(value)) ||
            null
          }
          onChange={(_event, selected) =>
            onChange({ target: { name, value: selected ? selected.value : "" } })
          }
          sx={{
            width: "100%",
            minWidth: 220,
            "& .MuiInputBase-root": { height: 40 },
            ...style,
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={placeholder}
              inputProps={{ ...params.inputProps, "aria-label": title || placeholder }}
            />
          )}
        />
      ) : (
        <Select
          id={name}
          name={name}
          size="small"
          value={selectValue}
          onChange={onChange}
          displayEmpty
          disabled={disabled}
          fullWidth
          inputProps={{ "aria-label": title || placeholder }}
          sx={{ height: 40, minWidth: 220, ...style }}
          endAdornment={
            clearable && selectValue ? (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange({ target: { name, value: "" } });
                }}
                sx={{
                  position: "absolute",
                  right: "28px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  padding: "2px",
                  color: "rgba(0, 0, 0, 0.38)",
                  "&:hover": {
                    backgroundColor: "transparent",
                    color: "#ef4444",
                  },
                }}
              >
                <CloseIcon fontSize="small" sx={{ fontSize: "16px" }} />
              </IconButton>
            ) : null
          }
        >
          <MenuItem value="" disabled={!clearable}>
            {placeholder}
          </MenuItem>
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      )}
    </div>
  );
};

export default SelectInput;
